import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { getImageUrl } from '@/utils/imagePaths';
import { toYMD } from '../utils/fechaLocal';

type Task = {
  id: string;
  title: string;
  detail?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  dueAt?: string | null;
  reminderAt?: string | null;
  assignedTo: string;
  assigneeName?: string | null;
  attachmentUrl?: string | null;
  createdBy: string;
  createdAt: string;
};

type Assignee = {
  id: string;
  name: string;
};

type TaskMention = {
  type: 'user' | 'client' | 'appointment' | 'budget';
  id: string;
  label: string;
};

type MentionCandidate = TaskMention & { group?: string };

type TaskFormState = {
  title: string;
  detail: string;
  mentions: TaskMention[];
  priority: Task['priority'];
  status: Task['status'];
  assignedTo: string;
  dueDate: string;
  dueTime: string;
  reminderEnabled: boolean;
  reminderDate: string;
  reminderTime: string;
};

const defaultFormState: TaskFormState = {
  title: '',
  detail: '',
  mentions: [],
  priority: 'normal',
  status: 'pending',
  assignedTo: '',
  dueDate: '',
  dueTime: '',
  reminderEnabled: false,
  reminderDate: '',
  reminderTime: ''
};

const toInputDate = (iso?: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toInputTime = (iso?: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const buildIso = (date: string, time: string) => {
  if (!date) return null;
  const timeValue = time || '00:00';
  return new Date(`${date}T${timeValue}`).toISOString();
};

const DatePicker = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);
  const dateValue = value ? new Date(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          captionLayout="dropdown"
          fromYear={1900}
          toYear={new Date().getFullYear() + 10}
          onSelect={(date: Date | undefined) => {
            onChange(date ? toYMD(date) : '');
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const NewTask: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { user, hasPermission } = useAuth();
  const canAssign = hasPermission('tasks_assign');

  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [formState, setFormState] = useState<TaskFormState>({
    ...defaultFormState,
    assignedTo: user?.id || ''
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [existingAttachment, setExistingAttachment] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  // Mention autocomplete
  const detailTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[]>([]);
  const [mentionDropdownOpen, setMentionDropdownOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIdx, setMentionStartIdx] = useState<number | null>(null);

  // Cargar candidatos al montar
  useEffect(() => {
    axios.get('/api/tasks/mention-candidates').then((res) => {
      setMentionCandidates(res.data?.candidates || []);
    }).catch(() => setMentionCandidates([]));
  }, []);

  const filteredMentionCandidates = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return mentionCandidates.slice(0, 30);
    return mentionCandidates.filter((c) => c.label.toLowerCase().includes(q));
  }, [mentionCandidates, mentionQuery]);

  const detectMention = (text: string, caretPos: number): { start: number; query: string } | null => {
    // Busca el último '@' antes del caret sin espacios entre medio
    let i = caretPos - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === '@') {
        // Verificar que sea inicio o esté precedido por espacio/salto de línea
        if (i === 0 || /\s/.test(text[i - 1])) {
          return { start: i, query: text.substring(i + 1, caretPos) };
        }
        return null;
      }
      if (/\s/.test(ch)) return null;
      i--;
    }
    return null;
  };

  const handleDetailChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const caret = e.target.selectionStart || 0;
    setFormState((prev) => ({ ...prev, detail: value }));

    const m = detectMention(value, caret);
    if (m) {
      setMentionStartIdx(m.start);
      setMentionQuery(m.query);
      setMentionDropdownOpen(true);
    } else {
      setMentionDropdownOpen(false);
      setMentionStartIdx(null);
    }
  };

  const handleDetailKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && mentionDropdownOpen) {
      e.preventDefault();
      setMentionDropdownOpen(false);
    }
  };

  const applyMention = (candidate: MentionCandidate) => {
    if (mentionStartIdx === null) return;
    const ta = detailTextareaRef.current;
    const text = formState.detail;
    const caret = ta?.selectionStart || text.length;
    const before = text.substring(0, mentionStartIdx);
    const after = text.substring(caret);
    const insertion = `@${candidate.label} `;
    const newText = before + insertion + after;
    setFormState((prev) => ({
      ...prev,
      detail: newText,
      mentions: [
        ...prev.mentions.filter((m) => !(m.type === candidate.type && m.id === candidate.id)),
        { type: candidate.type, id: candidate.id, label: candidate.label }
      ]
    }));
    setMentionDropdownOpen(false);
    setMentionStartIdx(null);
    setMentionQuery('');
    // Reposicionar caret después de la inserción
    setTimeout(() => {
      if (ta) {
        const pos = before.length + insertion.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const scopedAssignees = useMemo(() => {
    if (!assignees.length && user) {
      return [{ id: user.id, name: `${user.firstName} ${user.lastName}`.trim() || user.username }];
    }
    return assignees;
  }, [assignees, user]);

  const loadAssignees = useCallback(async () => {
    try {
      const res = await axios.get('/api/tasks/assignees');
      setAssignees(res.data?.users || []);
    } catch (error) {
      console.error('Error loading assignees', error);
    }
  }, []);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  // Cargar tarea existente si es edición
  useEffect(() => {
    const fetchTask = async () => {
      if (!isEdit || !id) return;
      setLoadingTask(true);
      try {
        const res = await axios.get(`/api/tasks/${id}`);
        const task: Task = res.data?.task;
        if (!task) {
          toast.error('Tarea no encontrada');
          navigate('/tasks');
          return;
        }
        setFormState({
          title: task.title,
          detail: task.detail || '',
          mentions: Array.isArray((task as any).mentions) ? (task as any).mentions : [],
          priority: task.priority,
          status: task.status,
          assignedTo: task.assignedTo,
          dueDate: toInputDate(task.dueAt),
          dueTime: toInputTime(task.dueAt),
          reminderEnabled: Boolean(task.reminderAt),
          reminderDate: toInputDate(task.reminderAt),
          reminderTime: toInputTime(task.reminderAt)
        });
        setExistingAttachment(task.attachmentUrl || null);
      } catch (error: any) {
        console.error('Error fetching task:', error);
        toast.error(error.response?.data?.error || 'Error al cargar la tarea');
        navigate('/tasks');
      } finally {
        setLoadingTask(false);
      }
    };
    void fetchTask();
  }, [id, isEdit, navigate]);

  const handleSave = async () => {
    if (!formState.title.trim()) {
      toast.error('El titulo es obligatorio');
      return;
    }

    // Reconciliar las menciones: solo guardar las que aún están presentes en el texto
    const cleanedMentions = formState.mentions.filter((m) =>
      formState.detail.includes(`@${m.label}`)
    );

    const payload = {
      title: formState.title.trim(),
      detail: formState.detail,
      mentions: cleanedMentions,
      priority: formState.priority,
      status: formState.status,
      assignedTo: formState.assignedTo || user?.id,
      dueAt: buildIso(formState.dueDate, formState.dueTime),
      reminderAt: formState.reminderEnabled
        ? buildIso(formState.reminderDate, formState.reminderTime)
        : null
    };

    setSubmitting(true);
    try {
      let taskId = id;
      if (isEdit && id) {
        await axios.put(`/api/tasks/${id}`, payload);
        toast.success('Tarea actualizada');
      } else {
        const created = await axios.post('/api/tasks', payload);
        taskId = created.data?.task?.id || taskId;
        toast.success('Tarea creada');
      }

      if (attachmentFile && taskId) {
        try {
          const formData = new FormData();
          formData.append('attachment', attachmentFile);
          await axios.post(`/api/tasks/${taskId}/attachment`, formData);
          toast.success('Foto guardada');
        } catch (uploadError: any) {
          console.error('Error uploading task photo', uploadError);
          toast.warning('La tarea se guardó, pero la foto no se pudo subir.');
        }
      }

      navigate('/tasks');
    } catch (error: any) {
      console.error('Error saving task', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar la tarea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/tasks')}
          className="rounded-full"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isEdit ? 'Editar Tarea' : 'Nueva Tarea'}</h1>
          <p className="text-muted-foreground text-sm">
            {isEdit ? 'Actualizá los datos de la tarea' : 'Completá los datos de la tarea'}
          </p>
        </div>
      </div>

      {loadingTask ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Cargando tarea...
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Titulo y Detalle */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Titulo *</Label>
                <Input
                  value={formState.title}
                  onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: Revisión de stock"
                />
              </div>
              <div className="space-y-2">
                <Label>Detalle / Notas</Label>
                <p className="text-xs text-muted-foreground">
                  Tip: usa <code className="px-1 rounded bg-muted">-</code> al inicio de línea para listas con bullets, y <code className="px-1 rounded bg-muted">@</code> para mencionar usuarios, clientes, turnos o presupuestos.
                </p>
                <div className="relative">
                  <Textarea
                    ref={detailTextareaRef}
                    value={formState.detail}
                    onChange={handleDetailChange}
                    onKeyDown={handleDetailKeyDown}
                    placeholder={"Describí la tarea...\n- Item de checklist 1\n- Item de checklist 2\n\nMencioná con @"}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  {mentionDropdownOpen && filteredMentionCandidates.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full max-h-[260px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                      {Object.entries(
                        filteredMentionCandidates.slice(0, 30).reduce<Record<string, MentionCandidate[]>>((acc, c) => {
                          const g = c.group || 'Otros';
                          (acc[g] = acc[g] || []).push(c);
                          return acc;
                        }, {})
                      ).map(([group, items]) => (
                        <div key={group}>
                          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/40">{group}</div>
                          {items.map((c) => (
                            <div
                              key={`${c.type}-${c.id}`}
                              onMouseDown={(e) => { e.preventDefault(); applyMention(c); }}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                            >
                              <span className="text-foreground">{c.label}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formState.mentions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-xs text-muted-foreground">Menciones:</span>
                    {formState.mentions.map((m, i) => (
                      <span
                        key={`${m.type}-${m.id}-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
                      >
                        {m.type === 'user' && '👤 '}
                        {m.type === 'client' && '🧑‍🤝‍🧑 '}
                        {m.type === 'appointment' && '📅 '}
                        {m.type === 'budget' && '📋 '}
                        {m.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Foto */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Foto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                ref={attachmentInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => attachmentInputRef.current?.click()}
                >
                  Seleccionar foto
                </Button>
                <span className="text-sm text-muted-foreground">
                  {attachmentFile?.name || 'Sin archivo seleccionado'}
                </span>
              </div>
              {attachmentFile && (
                <img
                  src={URL.createObjectURL(attachmentFile)}
                  alt="Vista previa"
                  className="w-full max-w-xs rounded-lg object-cover border border-border"
                />
              )}
              {existingAttachment && !attachmentFile && (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <span>Foto actual</span>
                  <img
                    src={getImageUrl(existingAttachment)}
                    alt="Foto de la tarea"
                    className="w-full max-w-xs rounded-lg object-cover border border-border"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Configuración */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configuración</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select
                    value={formState.priority}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, priority: value as Task['priority'] }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select
                    value={formState.status}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, status: value as Task['status'] }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="in_progress">En progreso</SelectItem>
                      <SelectItem value="completed">Completada</SelectItem>
                      <SelectItem value="cancelled">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Select
                  value={formState.assignedTo}
                  onValueChange={(value) => setFormState((prev) => ({ ...prev, assignedTo: value }))}
                  disabled={!canAssign}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {scopedAssignees.map((assignee) => (
                      <SelectItem key={assignee.id} value={assignee.id}>
                        {assignee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!canAssign && (
                  <p className="text-xs text-muted-foreground">Solo podés asignarte tareas a vos.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Fecha y Recordatorio */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fecha y Recordatorio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <DatePicker
                    value={formState.dueDate}
                    onChange={(value) => setFormState((prev) => ({ ...prev, dueDate: value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horario</Label>
                  <Input
                    type="time"
                    value={formState.dueTime}
                    onChange={(e) => setFormState((prev) => ({ ...prev, dueTime: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Recordatorio</Label>
                  <Switch
                    checked={formState.reminderEnabled}
                    onCheckedChange={(checked) =>
                      setFormState((prev) => ({ ...prev, reminderEnabled: checked }))
                    }
                  />
                </div>
                {formState.reminderEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DatePicker
                      value={formState.reminderDate}
                      onChange={(value) => setFormState((prev) => ({ ...prev, reminderDate: value }))}
                    />
                    <Input
                      type="time"
                      value={formState.reminderTime}
                      onChange={(e) => setFormState((prev) => ({ ...prev, reminderTime: e.target.value }))}
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  El sistema notificará al responsable cuando llegue el recordatorio.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/tasks')}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={submitting}>
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Guardando...
                </span>
              ) : (
                isEdit ? 'Actualizar Tarea' : 'Crear Tarea'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewTask;
